/* Petifo · Variant cards — drives the native <variant-picker>.

   The cards NEVER touch the cart. They set the theme's own radios/selects and
   dispatch ONE `change`; variant-picker.js then fetches + morphs and updates the
   price, gallery and buy button. Read-only sync back, so no loops.

   READ THE SELECTION FROM THE PICKER, NEVER FROM THE BUY FORM. This caused the
   worst bug of the lot: "jag behöver klicka 2-3 gånger och den väljs fram och
   tillbaka". currentValues() briefly read the form's hidden variant id —
   authoritative, but it only updates AFTER the theme's ~900ms fetch + morph. Our
   syncAll() then ran at 60ms and 650ms, read the STALE id and reset the
   highlight to the previous value, so a click visibly bounced back and had to be
   repeated. The picker's radios are set by us synchronously and are correct
   immediately, so currentValues() reads them PER OPTION INDEX and fills any
   option with no control (single value) from DATA. The form id is a last resort.

   ONE CHANGE EVENT PER CLICK. On a sparse matrix a pick moves several options;
   dispatching change on each made the theme fetch and morph once per option.
   setOption(..., silent) sets inputs quietly and onPick fires a single change at
   the end — one fetch, no intermediate invalid state.

   SPEED. The theme needs ~450ms network + morph, so the price used to move a
   second after the click. markPicked() paints the selection at once,
   optimisticPrice() writes the new price straight from DATA, and scheduleSync()
   replaced eight queued passes per click with one quick + one late.
   optimisticPrice() BAILS when the sale state would flip (that changes which
   price block the theme shows) and only rewrites the leaf money spans — the
   "Regular price"/"Sale price" labels are SIBLINGS, so they cannot be eaten.
   /products/<handle>.js returns the PRESENTMENT currency (verified: 31800 cents
   where the page rendered "318,00 kr").

   MONEY FORMAT IS LEARNED FROM THE PAGE, and the page now renders through
   `money_without_trailing_zeros`. So fmtMoney() must drop ",00" itself, or a
   EUR market (where real decimals exist, so learnMoney() DOES find a separator)
   would flash "29,00 €" for ~450ms before the theme's morph corrected it to
   "29 €". Decide per amount, not from the learned sample. When learnMoney()
   found no separator — every visible price was round — fall back to one implied
   by the thousands separator, so a genuinely odd price is not rounded off.

   SPARSE MATRICES. Quantity[1 pc, 5 pcs, 10 pcs] x Size[Roll, 30x40] is six
   combinations but only three real variants. A value is sold out only when NO
   purchasable variant carries it (reachability) — never test the exact current
   combination. And picking such a value must move the OTHER options too, which
   is what Shopify's own picker does.

   OPTION INDEX != CONTROL INDEX. Horizon renders no control for a single-value
   option, so a 2-option product can expose 1 fieldset. controlForOption()
   matches a control by its VALUE SET, never by position. */
(function () {
  'use strict';

  var DATA = null;
  var MONEYFMT = null;

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function host() { return qs('[data-pf-vc]'); }

  function picker() {
    var h = host();
    if (!h) return null;
    var pid = h.getAttribute('data-product-id');
    return qs('variant-picker[data-product-id="' + pid + '"]') || qs('variant-picker');
  }

  function fieldsets(pk) { return qsa('.variant-option--buttons', pk); }
  function selects(pk) { return qsa('select.variant-option__select', pk); }

  function optionControls() {
    var pk = picker();
    if (!pk) return [];
    var fs = fieldsets(pk);
    if (fs.length) {
      return fs.map(function (f) {
        return { el: f, kind: 'radio', values: qsa('input[type="radio"]', f).map(function (r) { return r.value; }) };
      });
    }
    return selects(pk).map(function (s) {
      return { el: s, kind: 'select', values: qsa('option', s).map(function (o) { return o.value; }) };
    });
  }

  function controlForOption(index) {
    var ctrls = optionControls();
    if (!ctrls.length) return null;
    if (!DATA || !DATA.options || !DATA.options[index]) return ctrls[index] || null;
    var vals = DATA.options[index].values || [];
    for (var c = 0; c < ctrls.length; c++) {
      var cv = ctrls[c].values;
      if (cv.length !== vals.length) continue;
      var all = true;
      for (var k = 0; k < vals.length; k++) { if (cv.indexOf(vals[k]) === -1) { all = false; break; } }
      if (all) return ctrls[c];
    }
    return null;
  }

  function selectedVariant() {
    if (!DATA || !DATA.variants) return null;
    var inp = qs('product-form-component input[name="id"]') || qs('form input[name="id"]');
    var id = inp ? parseInt(inp.value, 10) : NaN;
    if (isNaN(id)) return null;
    for (var i = 0; i < DATA.variants.length; i++) {
      if (DATA.variants[i].id === id) return DATA.variants[i];
    }
    return null;
  }

  /* Instant, full-length, product-option order — see header. */
  function currentValues() {
    if (DATA && DATA.options && DATA.options.length) {
      var out = [];
      var complete = true;
      for (var i = 0; i < DATA.options.length; i++) {
        var ctrl = controlForOption(i);
        if (!ctrl) {
          var vs = DATA.options[i].values || [];
          if (vs.length === 1) { out.push(vs[0]); } else { out.push(null); complete = false; }
          continue;
        }
        if (ctrl.kind === 'radio') {
          var c = qs('input[type="radio"]:checked', ctrl.el);
          if (c) { out.push(c.value); } else { out.push(null); complete = false; }
        } else {
          out.push(ctrl.el.value);
        }
      }
      if (complete) return out;
    }

    var v = selectedVariant();
    if (v && v.options) return v.options.slice();

    var pk = picker();
    if (!pk) return [];
    var res = [];
    var fs = fieldsets(pk);
    if (fs.length) {
      fs.forEach(function (f) {
        var c = qs('input[type="radio"]:checked', f);
        res.push(c ? c.value : null);
      });
      return res;
    }
    selects(pk).forEach(function (s) { res.push(s.value); });
    return res;
  }

  /* Returns the element it changed (so onPick can fire ONE change), or null. */
  function setOption(index, value, silent) {
    var ctrl = controlForOption(index);
    if (!ctrl) return null;

    if (ctrl.kind === 'radio') {
      var inputs = qsa('input[type="radio"]', ctrl.el);
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value === value) {
          if (inputs[i].checked) return null;
          inputs[i].checked = true;
          if (!silent) inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
          return inputs[i];
        }
      }
      return null;
    }

    if (ctrl.el.value === value) return null;
    ctrl.el.value = value;
    if (!silent) ctrl.el.dispatchEvent(new Event('change', { bubbles: true }));
    return ctrl.el;
  }

  function mainPriceEl() {
    var nodes = qsa('.price__sale, .price__regular');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.classList.contains('price__hidden')) continue;
      if (n.closest('product-card')) continue;
      if (n.closest('sticky-add-to-cart')) continue;
      if (n.closest('.dialog-zoomed-gallery')) continue;
      if (n.closest('[data-pf-vc]')) continue;
      return n;
    }
    return null;
  }

  function learnMoney(text) {
    var s = String(text || '');
    var m = s.match(/[\d][\d\s., ’']*\d|\d/);
    if (!m) return null;
    var num = m[0];
    var thou = num.length > 1 ? num.charAt(1) : '';
    var dec = num.length > 3 ? num.charAt(num.length - 3) : '';
    if (/\d/.test(thou)) thou = '';
    if (/\d/.test(dec)) dec = '';
    return { prefix: s.slice(0, m.index), suffix: s.slice(m.index + num.length), thou: thou, dec: dec };
  }

  /* Zero decimals are dropped to match money_without_trailing_zeros — see header. */
  function fmtMoney(cents) {
    if (!MONEYFMT || cents == null) return null;
    var c = Math.round(cents);
    var whole = (c % 100) === 0;
    var parts = (c / 100).toFixed(whole ? 0 : 2).split('.');
    var ip = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, MONEYFMT.thou || '');
    var sep = MONEYFMT.dec;
    if (!sep) sep = (MONEYFMT.thou === '.' || MONEYFMT.thou === ' ' || MONEYFMT.thou === ' ') ? ',' : '.';
    return MONEYFMT.prefix + ip + (parts[1] ? sep + parts[1] : '') + MONEYFMT.suffix;
  }

  function optimisticPrice(variant) {
    if (!variant) return;
    var root = mainPriceEl();
    if (!root) return;
    var cmp = qs('.compare-at-price', root);
    var saleNow = !!cmp;
    var saleNew = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
    if (saleNow !== saleNew) return;
    var leaf = qs('.price-item--sale', root) || qs('.price', root);
    if (!leaf) return;
    if (!MONEYFMT) MONEYFMT = learnMoney(leaf.textContent);
    var main = fmtMoney(variant.price);
    if (main == null) return;
    leaf.textContent = main;
    if (saleNew && cmp) {
      var was = fmtMoney(variant.compare_at_price);
      if (was != null) cmp.textContent = was;
    }
    updHeadPrice();
  }

  function updHeadPrice() {
    var h = host();
    if (!h) return;
    var slot = qs('[data-pf-headprice]', h);
    if (!slot) return;
    var src = mainPriceEl();
    if (!src) { slot.innerHTML = ''; return; }
    var clone = src.cloneNode(true);
    qsa('.visually-hidden, .price-off-badge', clone).forEach(function (n) { n.parentNode.removeChild(n); });
    slot.innerHTML = clone.innerHTML;
  }

  function syncAll() {
    var h = host();
    if (!h) return;
    var vals = currentValues();

    qsa('[data-pf-chosen]', h).forEach(function (el) {
      var i = parseInt(el.getAttribute('data-pf-chosen'), 10);
      if (!isNaN(i) && vals[i]) el.textContent = vals[i];
    });

    qsa('.pf-vc__card[data-opt-index], .pf-vp__pill[data-opt-index]', h).forEach(function (el) {
      var i = parseInt(el.getAttribute('data-opt-index'), 10);
      var v = el.getAttribute('data-opt-value');
      el.classList.toggle('is-selected', vals[i] === v);
    });

    qsa('.pf-vc__card[data-options]', h).forEach(function (el) {
      var opts = (el.getAttribute('data-options') || '').split('~|~');
      var match = opts.length === vals.length;
      for (var i = 0; match && i < opts.length; i++) {
        if (opts[i] !== vals[i]) match = false;
      }
      el.classList.toggle('is-selected', match);
    });

    refreshAvailability(vals);
    updHeadPrice();
  }

  var tQuick = null, tLate = null;
  function scheduleSync() {
    if (tQuick) clearTimeout(tQuick);
    if (tLate) clearTimeout(tLate);
    tQuick = setTimeout(syncAll, 60);
    tLate = setTimeout(syncAll, 650);
  }

  function markPicked(el) {
    var h = host();
    if (!h) return;
    var idx = el.getAttribute('data-opt-index');
    if (idx != null) {
      qsa('[data-opt-index="' + idx + '"]', h).forEach(function (o) {
        o.classList.toggle('is-selected', o === el);
      });
      var chosen = qs('[data-pf-chosen="' + idx + '"]', h);
      var val = el.getAttribute('data-opt-value');
      if (chosen && val) chosen.textContent = val;
      return;
    }
    qsa('.pf-vc__card[data-variant-id]', h).forEach(function (o) {
      o.classList.toggle('is-selected', o === el);
    });
  }

  function variantFor(options) {
    if (!DATA || !DATA.variants) return null;
    for (var i = 0; i < DATA.variants.length; i++) {
      var v = DATA.variants[i];
      var ok = true;
      for (var j = 0; j < options.length; j++) {
        if (options[j] != null && v.options[j] !== options[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  function firstAvailableWith(index, value) {
    if (!DATA || !DATA.variants) return null;
    for (var i = 0; i < DATA.variants.length; i++) {
      var v = DATA.variants[i];
      if (v.options[index] === value && v.available !== false) return v;
    }
    return null;
  }

  function refreshAvailability(vals) {
    if (!DATA) return;
    var h = host();
    if (!h) return;

    qsa('.pf-vc__card[data-opt-index], .pf-vp__pill[data-opt-index]', h).forEach(function (el) {
      var i = parseInt(el.getAttribute('data-opt-index'), 10);
      var v = el.getAttribute('data-opt-value');
      var probe = vals.slice();
      probe[i] = v;
      var direct = variantFor(probe);
      var reachable = (direct && direct.available !== false) || !!firstAvailableWith(i, v);
      el.classList.toggle('is-soldout', !reachable);
    });

    qsa('.pf-vc__card[data-variant-id]', h).forEach(function (el) {
      var id = parseInt(el.getAttribute('data-variant-id'), 10);
      for (var i = 0; i < DATA.variants.length; i++) {
        if (DATA.variants[i].id === id) {
          el.classList.toggle('is-soldout', DATA.variants[i].available === false);
          return;
        }
      }
    });
  }

  function loadData() {
    var h = host();
    if (!h || DATA) return;
    var handle = h.getAttribute('data-handle');
    if (!handle) return;
    fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) { DATA = j; syncAll(); })
      .catch(function () {});
  }

  function onPick(el) {
    if (el.classList.contains('is-soldout')) return;

    markPicked(el);

    var next = null;
    var touched = [];
    var t, i;

    if (el.hasAttribute('data-options')) {
      var opts = (el.getAttribute('data-options') || '').split('~|~');
      next = variantFor(opts);
      for (i = 0; i < opts.length; i++) { t = setOption(i, opts[i], true); if (t) touched.push(t); }
    } else {
      var idx = parseInt(el.getAttribute('data-opt-index'), 10);
      var val = el.getAttribute('data-opt-value');
      if (!isNaN(idx) && val != null) {
        var probe = currentValues();
        probe[idx] = val;
        var direct = variantFor(probe);
        var target = (direct && direct.available !== false) ? null : firstAvailableWith(idx, val);
        if (target) {
          next = target;
          for (i = 0; i < target.options.length; i++) { t = setOption(i, target.options[i], true); if (t) touched.push(t); }
        } else {
          next = direct;
          t = setOption(idx, val, true);
          if (t) touched.push(t);
        }
      }
    }

    /* ONE change for the whole pick — see header. */
    if (touched.length) {
      touched[touched.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
    }

    optimisticPrice(next);
    scheduleSync();
  }

  function isCompact() {
    var h = host();
    return !!h && h.classList.contains('pf-vc--compact');
  }

  function updArrows(wrap) {
    if (isCompact()) { wrap.classList.add('pf-vc--noscroll'); return; }
    var rail = qs('[data-pf-vc-rail]', wrap);
    if (!rail) return;
    var max = rail.scrollWidth - rail.clientWidth;
    wrap.classList.toggle('pf-vc--noscroll', max <= 4);
    wrap.classList.toggle('pf-vc--start', rail.scrollLeft <= 4);
    wrap.classList.toggle('pf-vc--end', rail.scrollLeft >= max - 4);
  }

  function step(rail) {
    var cards = qsa('.pf-vc__card', rail);
    if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
    if (cards.length === 1) return cards[0].getBoundingClientRect().width;
    return 100;
  }

  function page(wrap, dir) {
    var rail = qs('[data-pf-vc-rail]', wrap);
    if (!rail) return;
    var st = step(rail) || 100;
    var perView = Math.max(1, Math.floor(rail.clientWidth / st));
    var idx = Math.round(rail.scrollLeft / st) + dir * perView;
    var target = idx * st;
    var max = rail.scrollWidth - rail.clientWidth;
    if (target < 0) target = 0;
    if (target > max) target = max;
    rail.scrollTo({ left: target, behavior: 'smooth' });
  }

  function initialReveal() {
    syncAll();
    if (isCompact()) return;
    qsa('.pf-vc__railwrap').forEach(function (wrap) {
      var rail = qs('[data-pf-vc-rail]', wrap);
      if (!rail) return;
      var card = qs('.pf-vc__card.is-selected', rail);
      if (!card) return;
      var cl = card.offsetLeft;
      var cr = cl + card.offsetWidth;
      var vl = rail.scrollLeft;
      var vr = vl + rail.clientWidth;
      if (cl >= vl - 10 && cr <= vr + 10) return;
      var target = cl - (rail.clientWidth - card.offsetWidth) / 2;
      var max = rail.scrollWidth - rail.clientWidth;
      if (target < 0) target = 0;
      if (target > max) target = max;
      rail.scrollLeft = target;
      updArrows(wrap);
    });
  }

  var wired = false;

  function init() {
    var h = host();
    if (!h || wired) return;
    wired = true;

    document.addEventListener('click', function (e) {
      var el = e.target.closest && e.target.closest('.pf-vc__card, .pf-vp__pill');
      if (!el || !el.closest('[data-pf-vc]')) return;
      e.preventDefault();
      onPick(el);
    });

    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-pf-vc-arrow]');
      if (!a) return;
      e.preventDefault();
      var wrap = a.closest('.pf-vc__railwrap');
      if (wrap) page(wrap, parseInt(a.getAttribute('data-pf-vc-arrow'), 10) || 1);
    });

    qsa('.pf-vc__railwrap').forEach(function (wrap) {
      var rail = qs('[data-pf-vc-rail]', wrap);
      if (!rail) return;
      rail.addEventListener('scroll', function () { updArrows(wrap); }, { passive: true });
      try { new ResizeObserver(function () { updArrows(wrap); }).observe(rail); } catch (e) {}
      updArrows(wrap);
    });

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      if ((t.closest && t.closest('variant-picker')) || (t.classList && t.classList.contains('pf-sticky-variant'))) {
        scheduleSync();
      }
    }, true);

    try {
      var scope = h.closest('.shopify-section') || document.body;
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; updHeadPrice(); }, 120);
      }).observe(scope, { childList: true, subtree: true });
    } catch (e) {}

    loadData();
    [0, 150, 450, 900].forEach(function (ms) { setTimeout(initialReveal, ms); });
  }

  var tries = 0;
  var iv = setInterval(function () {
    tries++;
    init();
    if (wired || tries > 40) clearInterval(iv);
  }, 300);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();